import React, { FC, useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import {
	DrilldownState,
	isValidReferrer
} from 'src/apps/dashboard/components/accessibility-summary/helpers/drilldownLinkHelper';
import {
	ALL_ISSUES_TABLE_ID,
	UNIQUE_ISSUES_TABLE_ID
} from 'src/apps/dashboard/constants/Dashboard';
import {
	getMobileIssuesFilterDefinitions,
	MOBILE_FILTER_DEFINITIONS
} from 'src/apps/dashboard/constants/MobileDashboardDefaults';
import { buildLabelsExcludedFromDashboard } from 'src/apps/dashboard/helpers/ConfigurationProcessingHelpers';
import { useMobileAppIdFilterOptionsQuery } from 'src/apps/dashboard/hooks/useMobileAppIdFilterOptionsQuery';
import { useMobileDashboardDataQuery } from 'src/apps/dashboard/hooks/useMobileDashboardDataQuery';
import { useMobileScoreDataQuery } from 'src/apps/dashboard/hooks/useMobileScoreDataQuery';
import { useMobileBusinessUnitsFromConfig } from 'src/apps/dashboard/stores/dashboardConfigStore';
import { DashboardTemplate } from 'src/apps/dashboard/templates/DashboardTemplate';
import { isConfigurationReady } from 'src/common/helpers/ConfigurationHelpers';
import { UNIQUE_QUERY_PARAM } from 'src/common/helpers/QueryParamsHelper';
import { RoutesHelper } from 'src/common/helpers/RoutesHelper';
import { usePlatformConfigInitializer } from 'src/common/hooks/usePlatformConfigInitializer';
import { DASHBOARD_LABELS } from 'src/common/labels/dashboard.labels';
import { useConfiguration } from 'src/common/providers/configurationProvider/ConfigurationProvider';
import { useTableState } from 'src/common/providers/dashboard/TableStateProvider';
import { usePlatformConfigStore } from 'src/common/stores/platformConfigStore';

import { buildIssuesPageTabs, renderIssuesTableContent } from './helpers/IssuesPageHelpers';
import {
	MOBILE_ALL_ISSUES_COLUMNS,
	MOBILE_UNIQUE_ISSUES_COLUMNS
} from './helpers/IssuesTableColumns';
import { useFiltersWithQueryParams } from './hooks/useFiltersWithQueryParams';
import { useMobileAllIssuesData } from './hooks/useMobileAllIssuesData';
import { useMobileUniqueIssuesData } from './hooks/useMobileUniqueIssuesData';
import {
	HEADER as ISSUES_PAGE_HEADER,
	ISSUES_PAGE_BOTTOM_KIND,
	ISSUES_PAGE_TABS_ID
} from './IssuesPage.constants';
import { ISSUES_PAGE_LABELS } from './IssuesPage.labels';

export const MobileIssuesPage: FC = () => {
	const location = useLocation();
	const history = useHistory();
	const { getTableStateById } = useTableState();

	usePlatformConfigInitializer();

	const { configuration } = useConfiguration();
	const { isUniquenessServiceEnabled, mobileCoreLabels, unitIdDisplayName } =
		usePlatformConfigStore();
	const filterDefinitions = useMemo(
		() => getMobileIssuesFilterDefinitions(unitIdDisplayName),
		[unitIdDisplayName]
	);
	const labelsExcludedFromDashboard = useMemo(
		() => buildLabelsExcludedFromDashboard(mobileCoreLabels),
		[mobileCoreLabels]
	);

	const { businessUnits } = useMobileBusinessUnitsFromConfig();
	const mobileBuIds = useMemo(() => businessUnits.map((bu) => bu.id), [businessUnits]);

	const appIdFilterOptions = useMobileAppIdFilterOptionsQuery();
	const unitIdOptions = useMemo(
		() => businessUnits.map((bu) => ({ value: bu.id, label: bu.name })),
		[businessUnits]
	);
	const filterOptions = useMemo(
		() => ({ ...appIdFilterOptions, unitId: unitIdOptions }),
		[appIdFilterOptions, unitIdOptions]
	);

	const { filters, handleFilterChange } = useFiltersWithQueryParams({
		filterDefinitions,
		filterOptions
	});

	const allTableState = getTableStateById(ALL_ISSUES_TABLE_ID);
	const uniqueTableState = getTableStateById(UNIQUE_ISSUES_TABLE_ID);

	const dashboardDataQuery = useMobileDashboardDataQuery(
		filters,
		MOBILE_FILTER_DEFINITIONS,
		labelsExcludedFromDashboard
	);
	const scoreDataQuery = useMobileScoreDataQuery(
		filters,
		MOBILE_FILTER_DEFINITIONS,
		labelsExcludedFromDashboard
	);

	const isUniqueFromUrl = useMemo(() => {
		const searchParams = new URLSearchParams(location.search);
		return searchParams.get(UNIQUE_QUERY_PARAM) === 'true';
	}, [location.search]);

	const activeTabIndex = isUniqueFromUrl ? 1 : 0;

	const {
		data: uniqueIssuesData,
		isFetching: isFetchingUnique,
		error: uniqueError
	} = useMobileUniqueIssuesData(
		filters,
		uniqueTableState.paginationInfo.page,
		uniqueTableState.paginationInfo.sizePerPage,
		uniqueTableState.tableSort?.[0],
		mobileBuIds,
		isUniqueFromUrl
	);

	const {
		data: allIssuesData,
		isFetching: isFetchingAll,
		error: allError
	} = useMobileAllIssuesData(
		filters,
		allTableState.paginationInfo.page,
		allTableState.paginationInfo.sizePerPage,
		allTableState.tableSort?.[0],
		mobileBuIds,
		!isUniqueFromUrl
	);

	const handleBackClick = useCallback((): void => {
		const { referrer } = (location.state as DrilldownState) ?? {};
		if (isValidReferrer(referrer)) {
			history.push(referrer);
		} else {
			history.push(RoutesHelper.getMobileAccessibilityDashboardPath());
		}
	}, [history, location.state]);

	const handleTabSelected = useCallback(
		(index: number) => {
			const searchParams = new URLSearchParams(location.search);
			if (index === 1) {
				searchParams.set(UNIQUE_QUERY_PARAM, 'true');
			} else {
				searchParams.delete(UNIQUE_QUERY_PARAM);
			}
			history.replace({
				pathname: location?.pathname,
				search: `?${searchParams.toString() ?? ''}`,
				state: location.state
			});
		},
		[history, location.pathname, location.search, location.state]
	);

	const { tabs, tabPanels } = useMemo(() => {
		const allTabContent = renderIssuesTableContent({
			columns: MOBILE_ALL_ISSUES_COLUMNS,
			isFetching: isFetchingAll,
			error: allError,
			data: allIssuesData,
			tableId: ALL_ISSUES_TABLE_ID,
			title: DASHBOARD_LABELS.ALL_ISSUES_TABLE_TITLE,
			errorMessage: DASHBOARD_LABELS.ALL_ISSUES_LOAD_ERROR
		});
		const uniqueTabContent = renderIssuesTableContent({
			columns: MOBILE_UNIQUE_ISSUES_COLUMNS,
			isFetching: isFetchingUnique,
			error: uniqueError,
			data: uniqueIssuesData,
			tableId: UNIQUE_ISSUES_TABLE_ID,
			title: DASHBOARD_LABELS.UNIQUE_ISSUES_TABLE_TITLE,
			errorMessage: DASHBOARD_LABELS.UNIQUE_ISSUES_LOAD_ERROR
		});

		return buildIssuesPageTabs({
			activeTabIndex,
			onTabSelected: handleTabSelected,
			allTabContent,
			uniqueTabContent
		});
	}, [
		activeTabIndex,
		allError,
		allIssuesData,
		handleTabSelected,
		isFetchingAll,
		isFetchingUnique,
		uniqueError,
		uniqueIssuesData
	]);

	const header = useMemo(
		() => ({
			...ISSUES_PAGE_HEADER,
			backLink: {
				...ISSUES_PAGE_HEADER.backLink,
				url: RoutesHelper.getMobileAccessibilityDashboardPath(),
				text: ISSUES_PAGE_LABELS.BACK_TO_MOBILE_DASHBOARD,
				onClick: handleBackClick
			}
		}),
		[handleBackClick]
	);

	const filter = useMemo(
		() => ({
			filters,
			filterDefinitions,
			filterOptions,
			onFilterChange: handleFilterChange,
			filterPreset: 'issues' as const
		}),
		[filterDefinitions, filterOptions, filters, handleFilterChange]
	);

	const scoreQuery = useMemo(
		() => ({
			data: scoreDataQuery.data,
			isLoading: scoreDataQuery.isLoading,
			error: scoreDataQuery.error
		}),
		[scoreDataQuery.data, scoreDataQuery.isLoading, scoreDataQuery.error]
	);

	const dashboardQuery = useMemo(
		() => ({
			data: dashboardDataQuery.data,
			isLoading: dashboardDataQuery.isLoading,
			error: dashboardDataQuery.error
		}),
		[dashboardDataQuery.data, dashboardDataQuery.isLoading, dashboardDataQuery.error]
	);

	const bottom = useMemo(
		() => ({
			kind: ISSUES_PAGE_BOTTOM_KIND,
			tabState: {
				tabs,
				tabPanels,
				activeTabIndex,
				onTabSelected: handleTabSelected,
				tabsId: ISSUES_PAGE_TABS_ID
			}
		}),
		[tabs, tabPanels, activeTabIndex, handleTabSelected]
	);

	if (!isConfigurationReady(configuration)) {
		return null;
	}

	return (
		<DashboardTemplate
			header={header}
			filter={filter}
			effectiveFilters={filters}
			scoreQuery={scoreQuery}
			dashboardQuery={dashboardQuery}
			isUniquenessServiceEnabled={isUniquenessServiceEnabled}
			bottom={bottom}
		/>
	);
};
